/**
 * Focus — focus trap and keyboard navigation utilities.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
export function getFocusableElements(container) {
  return /** @type {HTMLElement[]} */ ([...container.querySelectorAll(FOCUSABLE_SELECTOR)]).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  );
}

/**
 * Trap focus within a container
 * @param {HTMLElement} container
 * @returns {{ destroy: () => void }}
 */
export function trapFocus(container) {
  const focusableElements = getFocusableElements(container);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleKeydown(event) {
    if (event.key !== 'Tab') return;

    const updatedFocusable = getFocusableElements(container);
    const first = updatedFocusable[0];
    const last = updatedFocusable[updatedFocusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);
  firstFocusable?.focus();

  return {
    destroy() {
      container.removeEventListener('keydown', handleKeydown);
    },
  };
}

/**
 * Save and restore focus (useful for modals/drawers)
 * @returns {{ restore: () => void }}
 */
export function saveFocus() {
  const previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

  return {
    restore() {
      previouslyFocused?.focus();
    },
  };
}
